import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { SharedProfileService } from 'src/app/components/profile/shared-profile.service';
import { InOutAnimation } from 'src/app/shared/animations/animations';
import { AnimatedLoaderType } from 'src/app/shared/models/animated-loader-type.enum';
import { AuthenticationService } from 'src/app/shared/services';
import { AppwriteService } from 'src/app/shared/services/appwrite.sdk.service';
import { selectIsWebhookComplete } from 'src/app/shared/store/_features/user/auth/auth.selectors';
//
import {
  AccountSetupHelperService,
  OnboardingWizardSetupStepsEnum,
} from './account-setup-helper.service';

export class OnboardingWizardSetupStep {
  enum: OnboardingWizardSetupStepsEnum;
  actionButtons?: {
    next: {
      disabled: boolean;
      label: string;
    };
    skip: {
      disabled: boolean;
      label: string;
    };
    back: {
      disabled: boolean;
      label: string;
    };
  };
}

@Component({
  selector: 'app-account-setup-wizard',
  templateUrl: './account-setup-wizard.component.html',
  styleUrls: ['./account-setup-wizard.component.css'],
  animations: [InOutAnimation],
})
export class AccountSetupWizardComponent implements OnInit, OnDestroy {
  animatedLoaderType = AnimatedLoaderType;

  processing: boolean = false;

  isWebhookDone: boolean = false;
  onboardingWizardSetupStepsEnum = OnboardingWizardSetupStepsEnum;

  constructor(
    public sharedProfileService: SharedProfileService,
    private authService: AuthenticationService,
    private toastr: ToastrService,
    private store: Store,
    public accountSetupHelperService: AccountSetupHelperService,
    private cdr: ChangeDetectorRef,
  ) {
    this.store.select(selectIsWebhookComplete).subscribe((value) => {
      this.isWebhookDone = value;
      this.cdr.markForCheck();
    });
  }

  /**
   * Flush progress immediately on page unload/refresh so a refresh within
   * the 800 ms debounce window never loses data.
   */
  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.accountSetupHelperService.flushSaveProgress();
  }

  ngOnDestroy(): void {
    clearTimeout((this.accountSetupHelperService as any)['_saveDebounceTimer']);
  }

  submit() {}
  onClickNext() {
    this.submit();
    this.nextStep();
    console.log(this.accountSetupHelperService.wizardData);
  }

  onClickBack() {
    this.accountSetupHelperService.wizardModel.goToPreviousStep();
    // Flush immediately on step navigation — don't wait for the 800 ms debounce
    this.accountSetupHelperService.flushSaveProgress();
  }

  nextStep() {
    this.setProcessing(true);
    setTimeout(() => {
      this.accountSetupHelperService.wizardModel.goToNextStep();
      // Flush immediately on step navigation — don't wait for the 800 ms debounce
      this.accountSetupHelperService.flushSaveProgress();

      setTimeout(() => {
        this.setProcessing(false);
      }, 2500);
    }, 500);
  }

  async finalStep() {
    this.setProcessing(true);
    setTimeout(async () => {
      await this.onSkip();
      this.setProcessing(false);
    }, 500);
  }

  setProcessing(value: boolean) {
    if (value) {
      this.accountSetupHelperService.setAiLoaderState('writing');
    } else {
      this.accountSetupHelperService.setAiLoaderState('idle');
    }
    this.processing = value;
  }

  async onSkip() {
    // Clear saved progress and mark onboarding complete
    this.accountSetupHelperService.clearSavedProgress();
    this.authService.markWebsiteOnboardingAsDone();
  }

  async ngOnInit() {
    this.isWebhookDone = await this.authService.isWebhookDone();

    // 1. Restore saved hubInput data BEFORE building the WizardModel
    const savedStepIndex = await this.accountSetupHelperService.restoreProgress();

    // 2. Build the WizardModel (reads the now-restored wizardData.hubInput)
    this.accountSetupHelperService.initializeWizardProductCreate();

    // 3. If we have a saved step, jump directly to it and mark prior steps done
    if (savedStepIndex > 0) {
      const model = this.accountSetupHelperService.wizardModel;
      for (let i = 0; i < savedStepIndex; i++) {
        model.markStepAsDone(i);
        model.enableStepNavigateByClick(i);
      }
      model.setCurrentStepByIndex(savedStepIndex);
    }

    this.cdr.detectChanges();
  }
}
